import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /** @type {Map<string, {line: number, quantity: number, action: string}>} */
  #pendingQuantityUpdates = new Map();

  /** @type {number | undefined} */
  #quantityBatchTimer;

  #quantityRequestInFlight = false;

  #externalRefreshRequested = false;

  /** @type {Map<HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement, {disabled: boolean, ariaDisabled: string | null}>} */
  #lockedCartControls = new Map();

  #cartInteractionsLocked = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    window.clearTimeout(this.#quantityBatchTimer);
    this.#quantityBatchTimer = undefined;
    this.#pendingQuantityUpdates.clear();
    this.#setCartInteractionLock(false);
  }

  /**
   * Collects the final desired quantity for every changed line. The component
   * owns a single debounce window, so changing line B cannot cancel line A.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const line = Number(event.detail.cartLine);
    if (!Number.isInteger(line) || line <= 0) return;

    const row = event.target instanceof Element ? event.target.closest('.cart-items__table-row') : null;
    const lineItemKey = row instanceof HTMLElement ? row.dataset.key : undefined;
    if (!lineItemKey) return;
    const quantity = Number(event.detail.quantity);
    if (!Number.isFinite(quantity) || quantity < 0) return;

    this.#enqueueQuantityUpdate(lineItemKey, {
      line,
      quantity,
      action: quantity === 0 ? 'clear' : 'change',
    });
  };

  /**
   * Stores only the newest intent for a stable Shopify line-item key and starts
   * one component-level batch timer. While a request is running, new intents
   * stay queued and are sent as the next serial batch.
   * @param {string} lineItemKey
   * @param {{line: number, quantity: number, action: string}} intent
   */
  #enqueueQuantityUpdate(lineItemKey, intent) {
    this.#pendingQuantityUpdates.set(lineItemKey, intent);
    const lineItemRow = this.#findCartItemRow(intent.line, lineItemKey);

    if (lineItemRow) {
      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }

    if (this.#quantityRequestInFlight) return;
    this.#scheduleQuantityBatch(300);
  }

  /** @param {number} delay */
  #scheduleQuantityBatch(delay) {
    window.clearTimeout(this.#quantityBatchTimer);
    this.#quantityBatchTimer = window.setTimeout(() => {
      this.#quantityBatchTimer = undefined;
      void this.#flushQuantityBatch();
    }, delay);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {string} [lineItemKey] - Stable Shopify line item key.
   */
  onLineItemRemove(line, lineItemKey) {
    // Deklaratywne `on:click` motywu przekazuje zdarzenie jako kolejny
    // argument. Akceptujemy wyłącznie prawdziwy string Shopify; w pozostałych
    // przypadkach pobieramy stabilny klucz z wiersza. Wysłanie obiektu Event
    // jako `id` kończyło się odpowiedzią 400 i brakiem usunięcia produktu.
    const explicitKey = typeof lineItemKey === 'string' && lineItemKey ? lineItemKey : undefined;
    const cartItemRowToRemove = this.#findCartItemRow(line, explicitKey);
    const stableKey = explicitKey || cartItemRowToRemove?.dataset.key;
    if (!stableKey) return;

    this.#enqueueQuantityUpdate(stableKey, { line, quantity: 0, action: 'clear' });

    if (!cartItemRowToRemove) return;

    // Nie usuwamy wiersza optymistycznie. Shopify najpierw potwierdza zmianę,
    // dzięki czemu błąd sieci nie zostawia fałszywie pustego koszyka.
    const textComponent = /** @type {TextComponent | undefined} */ (cartItemRowToRemove.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.lineItemKey] - Stable Shopify line item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const { line, lineItemKey, quantity, action } = config;
    const stableKey = lineItemKey || this.#findCartItemRow(line)?.dataset.key;
    if (!stableKey) return;
    this.#enqueueQuantityUpdate(stableKey, { line, quantity, action });
  }

  /**
   * Sends one serial /cart/update.js request for all final quantities collected
   * during the debounce window. Shopify line-item keys remain stable even when
   * another line is removed and its numeric index changes.
   */
  async #flushQuantityBatch() {
    if (this.#quantityRequestInFlight || this.#pendingQuantityUpdates.size === 0) return;

    const batch = new Map(this.#pendingQuantityUpdates);
    this.#pendingQuantityUpdates.clear();
    this.#quantityRequestInFlight = true;
    this.#disableCartItems();
    this.#clearCartErrors();

    const sectionId = this.sectionId;
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker('quantity-batch:user-action');
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const updates = Object.fromEntries(Array.from(batch, ([key, intent]) => [key, intent.quantity]));
    const payload = {
      updates,
      sections: Array.from(sectionsToUpdate),
      sections_url: window.location.pathname,
    };
    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    try {
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const parsedResponseText = await response.json();
      if (!response.ok || parsedResponseText.errors) {
        const detail = this.#normaliseCartError(parsedResponseText.errors || parsedResponseText.description);
        throw new Error(detail || `Cart update failed with status ${response.status}`);
      }

      const sectionHTML = parsedResponseText.sections?.[this.sectionId];
      if (typeof sectionHTML !== 'string') throw new Error('Cart section missing from update response');

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      resetShimmer(this);

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      await morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });
      this.#updateCartQuantitySelectorButtonStates();
      this.#setCartInteractionLock(true);
    } catch (error) {
      console.error(error);
      await this.#recoverCartState(
        0,
        'Nie udało się potwierdzić zmiany. Sprawdź aktualny stan koszyka i spróbuj ponownie.'
      );
      this.#externalRefreshRequested = false;
    } finally {
      this.#quantityRequestInFlight = false;

      if (this.#pendingQuantityUpdates.size > 0) {
        // Controls stay locked between serial batches. This branch is mainly a
        // safeguard for programmatic events because native controls are truly
        // disabled while the request is in flight.
        this.#scheduleQuantityBatch(0);
      } else {
        if (this.#externalRefreshRequested) {
          this.#externalRefreshRequested = false;
          try {
            await sectionRenderer.renderSection(sectionId, { cache: false });
            this.#setCartInteractionLock(true);
          } catch (refreshError) {
            console.error('Unable to refresh cart after a concurrent cart update', refreshError);
          }
        }
        this.#enableCartItems();
      }
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Converts Shopify's possible string/object error payload into readable text.
   * @param {unknown} errors
   * @returns {string}
   */
  #normaliseCartError(errors) {
    if (typeof errors === 'string') return errors.trim();
    if (Array.isArray(errors)) return errors.filter((error) => typeof error === 'string').join(' ').trim();
    if (errors && typeof errors === 'object') {
      return Object.values(errors)
        .flatMap((error) => (Array.isArray(error) ? error : [error]))
        .filter((error) => typeof error === 'string')
        .join(' ')
        .trim();
    }
    return '';
  }

  /**
   * Re-renders the section from Shopify after an ambiguous/failed request and
   * only then reports the error, so the visible cart remains the source of truth.
   * @param {number} line
   * @param {string} message
   */
  async #recoverCartState(line, message) {
    const sectionId = this.sectionId;
    this.#restoreQuantityInputs(this);
    resetShimmer(this);

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (recoveryError) {
      console.error('Unable to refresh cart state after an update error', recoveryError);
    }

    const section = document.getElementById(`shopify-section-${sectionId}`);
    const component = section?.querySelector('cart-items-component');
    const scope = component instanceof HTMLElement ? component : this;
    this.#restoreQuantityInputs(scope);
    this.#showCartError(scope, line, message);
  }

  /** @param {ParentNode} scope */
  #restoreQuantityInputs(scope) {
    for (const input of scope.querySelectorAll('input[data-cart-quantity]')) {
      if (input instanceof HTMLInputElement) input.value = input.defaultValue;
    }
  }

  /**
   * @param {HTMLElement} scope
   * @param {number} line
   * @param {string} message
   */
  #showCartError(scope, line, message) {
    const cartItemError = line > 0 ? scope.querySelector(`[ref="cartItemError-${line}"]`) : null;
    const cartItemErrorContainer = line > 0 ? scope.querySelector(`[ref="cartItemErrorContainer-${line}"]`) : null;

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
      return;
    }

    const section = scope.closest('.shopify-section');
    const pageAlert = section?.querySelector('[data-etolie-cart-alert]');
    if (pageAlert instanceof HTMLElement) {
      pageAlert.textContent = message;
      pageAlert.classList.remove('hidden');
      return;
    }

    let runtimeAlert = scope.querySelector('[data-etolie-cart-runtime-alert]');
    if (!(runtimeAlert instanceof HTMLElement)) {
      runtimeAlert = document.createElement('div');
      runtimeAlert.className = 'etolie-cart-runtime-alert';
      runtimeAlert.dataset.etolieCartRuntimeAlert = '';
      runtimeAlert.setAttribute('role', 'alert');
      runtimeAlert.setAttribute('aria-live', 'assertive');
      scope.prepend(runtimeAlert);
    }
    runtimeAlert.textContent = message;
  }

  #clearCartErrors() {
    for (const container of this.querySelectorAll('.cart-items__error:not(.hidden)')) {
      container.classList.add('hidden');
    }
    this.querySelector('[data-etolie-cart-runtime-alert]')?.remove();
    const pageAlert = this.closest('.shopify-section')?.querySelector('[data-etolie-cart-alert]');
    if (pageAlert instanceof HTMLElement) {
      pageAlert.textContent = '';
      pageAlert.classList.add('hidden');
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    try {
      // Własny, potwierdzony update został już wyrenderowany niżej w
      // updateQuantity(). Nie traktuj go jak nakładającej się obcej mutacji —
      // wymuszałoby to zbędny drugi morph i gubiło stan UI po odblokowaniu.
      if (event.target === this) return;

      if (
        this.#quantityRequestInFlight ||
        this.#pendingQuantityUpdates.size > 0 ||
        this.#quantityBatchTimer !== undefined
      ) {
        this.#externalRefreshRequested = true;
        return;
      }

      // Sekcję koszyka po zmianie kodu rabatowego morfuje cart-discount.js tą
      // samą odpowiedzią z /cart/update (razem z listą pozycji), a dopiero po
      // niej wypisuje komunikat o błędnym kodzie. Drugi render z tego miejsca
      // przychodził później i kasował ten komunikat, więc renderu tu nie ma.
      // Zdarzenie obsługujemy wyżej tylko po to, by odświeżyć sekcję po
      // zakończeniu trwającej partii zmian ilości.
      if (event instanceof DiscountUpdateEvent) return;

      const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
      if (cartItemsHtml) {
        await morphSection(this.sectionId, cartItemsHtml);

        // Update button states for all cart quantity selectors after morph
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
    } catch (error) {
      console.error(error);
      await this.#recoverCartState(
        0,
        'Nie udało się odświeżyć koszyka. Pokazujemy ostatni potwierdzony stan, spróbuj ponownie.'
      );
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.#setCartInteractionLock(true);
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.#setCartInteractionLock(false);
    // Morph może ponownie wykorzystać istniejące kontrolki. Wtedy zwykłe
    // odtworzenie stanu sprzed requestu przywróciłoby np. disabled z ilości 1
    // już po zmianie na 2, dlatego przeliczamy przyciski z aktualnej ilości.
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Disables every interactive cart control while an active quantity batch
   * settles, preserving pre-existing disabled and ARIA states.
   * @param {boolean} locked
   */
  #setCartInteractionLock(locked) {
    if (locked) {
      this.#cartInteractionsLocked = true;
      this.setAttribute('aria-busy', 'true');

      for (const control of this.querySelectorAll('input, button, select, textarea')) {
        if (
          !(control instanceof HTMLInputElement) &&
          !(control instanceof HTMLButtonElement) &&
          !(control instanceof HTMLSelectElement) &&
          !(control instanceof HTMLTextAreaElement)
        ) continue;

        if (!this.#lockedCartControls.has(control)) {
          this.#lockedCartControls.set(control, {
            disabled: control.disabled,
            ariaDisabled: control.getAttribute('aria-disabled'),
          });
        }
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    if (!this.#cartInteractionsLocked && this.#lockedCartControls.size === 0) return;
    this.#cartInteractionsLocked = false;
    this.removeAttribute('aria-busy');
    for (const [control, state] of this.#lockedCartControls) {
      if (!control.isConnected) continue;
      control.disabled = state.disabled;
      if (state.ariaDisabled === null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', state.ariaDisabled);
    }
    this.#lockedCartControls.clear();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * @param {number} line
   * @param {string} [lineItemKey]
   * @returns {HTMLTableRowElement | undefined}
   */
  #findCartItemRow(line, lineItemKey) {
    if (lineItemKey) {
      const matchingRow = this.refs.cartItemRows.find((row) => row.dataset.key === lineItemKey);
      if (matchingRow) return matchingRow;
    }
    return this.refs.cartItemRows[line - 1];
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
